import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Filter, Upload, X } from "react-feather";
import { FaPlusCircle } from "react-icons/fa";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FormLabel } from "react-bootstrap";
import { Button, Card, CardBody, Col, Input, Label, Media, Table } from "reactstrap";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import { useLoader } from "../../_helper/LoaderContext";
import MyPagination from "../pagination";
import ImportDoctorsModal from "./components/ImportDoctorsModal";
import PharmaDoctorModal from "./components/PharmaDoctorModal";
import {
	deletePharmaDoctorAPI,
	exportPharmaDoctorsAPI,
	pharmaDoctorListAPI,
	updatePharmaDoctorStatusAPI,
} from "./PharmaDoctorService";
import "./pharma-doctors-style.scss";

const getExportFileUrl = (response) => {
	const data = response?.data?.data;
	if (!data) return null;
	if (typeof data === "string") return data;
	return data.downloadLink || data.url || data.fileUrl || data.excelUrl || null;
};

const formatListDate = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const getDoctorId = (doctor) => doctor?._id || doctor?.doctorId || doctor?.id;

const parseListResponse = (response) => {
	const payload = response?.data?.data ?? response?.data ?? {};
	const doctors = payload.doctorList || [];
	const total = payload.totalPharmaDoctors || 0;

	return {
		doctors: Array.isArray(doctors) ? doctors : [],
		total: Number(total) || 0,
	};
};

const PharmaDoctors = () => {
	const { t } = useTranslation();
	const { setLoading } = useLoader();

	const [doctors, setDoctors] = useState([]);
	const [page, setPage] = useState(1);
	const [size, setSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);

	const [searchValue, setSearchValue] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [appliedFilters, setAppliedFilters] = useState({
		search: "",
		startDate: "",
		endDate: "",
	});
	const [isClearing, setIsClearing] = useState(false);

	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState("add");
	const [selectedDoctorId, setSelectedDoctorId] = useState(null);
	const [importModalOpen, setImportModalOpen] = useState(false);
	const [isExporting, setIsExporting] = useState(false);

	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	const [actionTooltips, setActionTooltips] = useState({});
	const [statusTooltips, setStatusTooltips] = useState({});

	const toggleActionTooltip = (id) => {
		setActionTooltips((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const toggleStatusTooltip = (id) => {
		setStatusTooltips((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const fetchDoctors = useCallback(async () => {
		// if (!isHospitalConnected) return;

		const body = {
			page,
			limit: size,
			search: appliedFilters.search?.trim() || "",
		};

		if (appliedFilters.startDate) {
			body.startDate = appliedFilters.startDate;
		}
		if (appliedFilters.endDate) {
			body.endDate = appliedFilters.endDate;
		}

		try {
			setLoading(true);
			const response = await pharmaDoctorListAPI(body);
			const { doctors: list, total } = parseListResponse(response);
			setDoctors(list);
			setTotalItems(Math.max(1, Math.ceil(total / size)));
		} catch (error) {
			console.error("Error fetching doctors:", error);
			setDoctors([]);
			setTotalItems(0);
		} finally {
			setLoading(false);
		}
	}, [appliedFilters, page, setLoading, size]);

	useEffect(() => {
		fetchDoctors();
	}, [fetchDoctors]);

	const handleSearch = () => {
		setPage(1);
		setAppliedFilters({
			search: searchValue,
			startDate,
			endDate,
		});
	};

	const handleClear = () => {
		setIsClearing(true);
		setSearchValue("");
		setStartDate("");
		setEndDate("");
		setPage(1);
		setAppliedFilters({ search: "", startDate: "", endDate: "" });
	};

	const handlePageChange = (pageNumber) => setPage(pageNumber);

	const handlePageSizeChange = (e) => {
		setSize(parseInt(e.target.value, 10));
		setPage(1);
	};

	useEffect(() => {
		if (isClearing) {
			setIsClearing(false);
		}
	}, [isClearing]);

	const openModal = (mode, doctorId = null) => {
		setModalMode(mode);
		setSelectedDoctorId(doctorId);
		setModalOpen(true);
	};

	const closeModal = () => {
		setModalOpen(false);
		setSelectedDoctorId(null);
	};

	const handleDelete = async (doctor) => {
		const doctorId = getDoctorId(doctor);
		if (!doctorId) return;

		const result = await Swal.fire({
			title: t("Are you sure?"),
			text: t("You want to delete this doctor?"),
			icon: "warning",
			showCancelButton: true,
			customClass: {
				confirmButton: "btn-primary",
				cancelButton: "btn-danger",
			},
			confirmButtonText: t("Yes, Delete It!"),
			cancelButtonText: t("Cancel"),
		});

		if (!result.isConfirmed) return;

		try {
			setLoading(true);
			const response = await deletePharmaDoctorAPI({ doctorId });
			toast.success(response?.data?.message || t("Doctor deleted successfully"));
			fetchDoctors();
		} catch (error) {
			console.error("Error deleting doctor:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleStatusChange = async (doctor, event) => {
		const doctorId = getDoctorId(doctor);
		if (!doctorId) return;

		const nextStatus = event.target.checked;
		const previousStatus = doctor.status === true || doctor.status === "active";

		event.target.checked = previousStatus;

		const result = await Swal.fire({
			icon: "warning",
			title: t("Are you sure"),
			text: nextStatus
				? t("You wish to activate this doctor?")
				: t("You wish to deactivate this doctor?"),
			showCancelButton: true,
			customClass: {
				confirmButton: "btn-primary",
				cancelButton: "btn-danger",
			},
			confirmButtonText: nextStatus ? t("Yes, Activate") : t("Yes, Deactivate"),
			cancelButtonText: t("Cancel"),
		});

		if (!result.isConfirmed) return;

		try {
			setLoading(true);
			const response = await updatePharmaDoctorStatusAPI({
				doctorId,
				status: nextStatus,
			});
			toast.success(response?.data?.message || t("Status updated successfully"));
			fetchDoctors();
		} catch (error) {
			console.error("Error updating doctor status:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleExportDoctors = async () => {
		try {
			setIsExporting(true);
			setLoading(true);

			const response = await exportPharmaDoctorsAPI({});

			if (response?.data?.status === "success") {
				const fileUrl = getExportFileUrl(response);

				if (fileUrl) {
					toast.success(
						response.data.message || t("Doctors exported successfully")
					);

					const link = document.createElement("a");
					link.href = fileUrl;
					link.download = "";
					link.target = "_blank";
					link.rel = "noopener noreferrer";

					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					toast.warning(t("Export file URL not found in response."));
				}
			}
		} catch (error) {
			console.error("Error exporting doctors:", error);
		} finally {
			setIsExporting(false);
			setLoading(false);
		}
	};

	const isDoctorActive = (doctor) =>
		doctor?.status === true || doctor?.status === "active";

	return (
		<Fragment>
			<div className="pharma-doctors-page">
				<Breadcrumbs
					mainTitle={t("Doctors")}
					parent={t("Dashboard")}
					title={t("Manage Doctors")}
				/>

				<Col sm="12">
					<Card className="filter-card border mb-3">
						<CardBody>
							<div className="row g-3 align-items-end">
								<Col xs={12} className="d-flex flex-wrap gap-2 justify-content-md-end">
									<Button
										color="primary"
										outline
										className="d-flex align-items-center gap-2"
										onClick={handleExportDoctors}
										disabled={isExporting}
									>
										<Download size={16} />
										{t("Export")}
									</Button>
									<Button
										color="primary"
										outline
										className="d-flex align-items-center gap-2"
										onClick={() => setImportModalOpen(true)}
									>
										<Upload size={16} />
										{t("Import")}
									</Button>
									<Button
										color="primary"
										className="d-flex align-items-center gap-2 add-doctor-btn"
										onClick={() => openModal("add")}
									>
										<FaPlusCircle size={16} />
										{t("Add Doctor")}
									</Button>
								</Col>
								<Col xs={12} md={4} lg={4}>
									<label className="form-label">{t("Search")}</label>
									<Input
										type="text"
										placeholder={t("Search by name, email, speciality...")}
										value={searchValue}
										onChange={(e) => setSearchValue(e.target.value)}
										onKeyDown={(e) => e.key === "Enter" && handleSearch()}
									/>
								</Col>
								<Col xs={12} sm={6} md={3} lg={3}>
									<label className="form-label">{t("Start Date")}</label>
									<Input
										type="date"
										value={startDate}
										max={new Date().toISOString().split('T')[0]}
										onChange={(e) => setStartDate(e.target.value)}
									/>
								</Col>
								<Col xs={12} sm={6} md={3} lg={3}>
									<label className="form-label">{t("End Date")}</label>
									<Input
										type="date"
										value={endDate}
										min={startDate || undefined}
										max={new Date().toISOString().split('T')[0]}
										onChange={(e) => setEndDate(e.target.value)}
									/>
								</Col>
								<Col
									xs={12}
									md={2}
									lg={2}
									className="d-flex filter-actions gap-2"
								>
									<Button
										id="pd-filter-tooltip"
										color="primary"
										onClick={handleSearch}
									>
										<Filter size={16} />
									</Button>
									<ToolTip
										attrToolTip={{
											placement: "top",
											isOpen: filterTooltipOpen,
											target: "pd-filter-tooltip",
											toggle: () => setFilterTooltipOpen(!filterTooltipOpen),
										}}
									>
										<strong>{t("Filter")}</strong>
									</ToolTip>
									<Button
										id="pd-clear-tooltip"
										color="secondary"
										onClick={handleClear}
									>
										<X size={16} />
									</Button>
									<ToolTip
										attrToolTip={{
											placement: "top",
											isOpen: clearTooltipOpen,
											target: "pd-clear-tooltip",
											toggle: () => setClearTooltipOpen(!clearTooltipOpen),
										}}
									>
										<strong>{t("Clear")}</strong>
									</ToolTip>
								</Col>
							</div>
						</CardBody>
					</Card>

					<Card>
						<CardBody>
							<div className="table-responsive">
								<Table bordered hover striped>
									<thead>
										<tr>
											<th>#</th>
											<th>{t("Doctor Name")}</th>
											<th>{t("Contact")}</th>
											<th>{t("Speciality")}</th>
											<th>{t("Clinic")}</th>
											<th>{t("Registration No.")}</th>
											<th>{t("Created On")}</th>
											<th className="text-center">{t("Status")}</th>
											<th className="text-center">{t("Action")}</th>
										</tr>
									</thead>
									<tbody>
										{doctors.length > 0 ? (
											doctors.map((doctor, idx) => {
												const doctorId = getDoctorId(doctor);
												const viewTipId = `pd-view-${doctorId}`;
												const editTipId = `pd-edit-${doctorId}`;
												const deleteTipId = `pd-delete-${doctorId}`;
												const statusTipId = `pd-status-${doctorId}`;

												return (
													<tr key={doctorId || idx}>
														<td>{(page - 1) * size + idx + 1}</td>
														<td>
															<div>{doctor.name || "-"}</div>
															{doctor.qualification && (
																<div className="text-muted small">
																	{doctor.qualification}
																</div>
															)}
														</td>
														<td>
															{doctor.email && (
																<div>{doctor.email}</div>
															)}
															{doctor.phoneNumber ? (
																<div className="text-muted small">
																	{`${doctor.phoneCode || ""} ${doctor.phoneNumber}`.trim()}
																</div>
															) : (
																<div>-</div>
															)}
														</td>
														<td>{doctor.speciality || doctor.specialty || "-"}</td>
														<td>
															<div>{doctor.clinicName || "-"}</div>
															{doctor.clinicAddress && (
																<div className="text-muted small">
																	{doctor.clinicAddress}
																</div>
															)}
														</td>
														<td>{doctor.registrationNumber || "-"}</td>
														<td>
															{formatListDate(
																doctor.createdAt || doctor.created_at
															)}
														</td>
														<td>
															<Media body className="text-center icon-state">
																<Label
																	className="switch"
																	id={statusTipId}
																	htmlFor={`pd-status-switch-${doctorId}`}
																>
																	<Input
																		type="checkbox"
																		id={`pd-status-switch-${doctorId}`}
																		checked={isDoctorActive(doctor)}
																		onChange={(e) =>
																			handleStatusChange(doctor, e)
																		}
																	/>
																	<span className="switch-state" />
																</Label>
															</Media>
															<ToolTip
																attrToolTip={{
																	placement: "top",
																	isOpen: statusTooltips[doctorId] || false,
																	target: statusTipId,
																	toggle: () => toggleStatusTooltip(doctorId),
																}}
															>
																<strong>{t("Change Status")}</strong>
															</ToolTip>
														</td>
														<td className="text-center text-nowrap">
															<button
																type="button"
																className="success-action-button me-1"
																id={viewTipId}
																onClick={() => openModal("view", doctorId)}
															>
																<i className="fa fa-eye" style={{ fontSize: "18px" }} />
															</button>
															<ToolTip
																attrToolTip={{
																	placement: "top",
																	isOpen: actionTooltips[viewTipId] || false,
																	target: viewTipId,
																	toggle: () => toggleActionTooltip(viewTipId),
																}}
															>
																<strong>{t("View")}</strong>
															</ToolTip>

															<button
																type="button"
																className="success-action-button me-1"
																id={editTipId}
																onClick={() => openModal("edit", doctorId)}
															>
																<i className="fa fa-pencil-square" style={{ fontSize: "18px" }} />
															</button>
															<ToolTip
																attrToolTip={{
																	placement: "top",
																	isOpen: actionTooltips[editTipId] || false,
																	target: editTipId,
																	toggle: () => toggleActionTooltip(editTipId),
																}}
															>
																<strong>{t("Edit")}</strong>
															</ToolTip>

															<button
																type="button"
																className="danger-action-button"
																id={deleteTipId}
																onClick={() => handleDelete(doctor)}
															>
																<i className="fa fa-trash" style={{ fontSize: "18px" }} />
															</button>
															<ToolTip
																attrToolTip={{
																	placement: "top",
																	isOpen: actionTooltips[deleteTipId] || false,
																	target: deleteTipId,
																	toggle: () => toggleActionTooltip(deleteTipId),
																}}
															>
																<strong>{t("Delete")}</strong>
															</ToolTip>
														</td>
													</tr>
												);
											})
										) : (
											<tr>
												<td colSpan={9} className="text-center text-muted p-3">
													{t("No doctors found")}
												</td>
											</tr>
										)}
									</tbody>
								</Table>
							</div>

							<div className="d-md-flex align-items-center justify-content-between mt-3">
								<div className="show-entries">
									<FormLabel>{t("Show")}&nbsp;</FormLabel>
									<select
										value={size}
										onChange={handlePageSizeChange}
										className="form-control"
										style={{ display: "inline", width: "auto" }}
									>
										<option value={5}>5</option>
										<option value={10}>10</option>
										<option value={20}>20</option>
									</select>
									&nbsp;<FormLabel>{t("entries")}</FormLabel>
								</div>
								<MyPagination
									totalPages={totalItems}
									currentPage={page}
									onPageChange={handlePageChange}
								/>
							</div>
						</CardBody>
					</Card>
				</Col>

				<PharmaDoctorModal
					show={modalOpen}
					onHide={closeModal}
					doctorId={selectedDoctorId}
					mode={modalMode}
					onSaved={fetchDoctors}
				/>

				<ImportDoctorsModal
					show={importModalOpen}
					onHide={() => setImportModalOpen(false)}
					onImported={fetchDoctors}
				/>
			</div>
		</Fragment>
	);
};

export default PharmaDoctors;
